import React from 'react';
import { Box, Paper, Typography, Chip, Alert } from '@mui/material';
import {
  DragIndicator as DragIcon,
  DescriptionOutlined as DocumentIcon,
  EditNoteOutlined as FieldIcon,
} from '@mui/icons-material';
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from '@hello-pangea/dnd';
import type { DocumentRequirement } from '@/types';
import type { FormField, FormSection } from './FormFieldEditor';

/** Section droppables are prefixed so a section drop is distinguishable from an item drop. */
const SECTION_DROPPABLE = 'sections';
const ITEM_DROPPABLE_PREFIX = 'section-items:';

const UNGROUPED = 'Övrigt';

/** One row in a section: either a document requirement or a form field. */
type SortItem =
  | { kind: 'requirement'; id: string; label: string; requirement: DocumentRequirement }
  | { kind: 'field'; id: string; label: string; field: FormField };

export interface SortingTabProps {
  fields: FormField[];
  sections: FormSection[];
  documentRequirements: DocumentRequirement[];
  /** Receives the full lists with displayOrder / order reassigned. */
  onChange: (next: {
    fields: FormField[];
    sections: FormSection[];
    documentRequirements: DocumentRequirement[];
  }) => void;
  disabled?: boolean;
}

/**
 * Resolve the section title a form field belongs to.
 * Mirrors getFormFieldDefinitions: fields carry sectionId, requirements carry the title.
 */
function fieldSectionTitle(field: FormField, sections: FormSection[]): string {
  if (!field.sectionId) return UNGROUPED;
  return sections.find((s) => s.id === field.sectionId)?.title ?? UNGROUPED;
}

/**
 * Build the ordered section list shown in the tab.
 *
 * Sections come from the registry in `order`, then any section that exists only as a
 * requirement's free-text `sectionName`. A field with no displayOrder sorts last, matching
 * how the render pages treat legacy data.
 */
function buildGroups(
  fields: FormField[],
  sections: FormSection[],
  documentRequirements: DocumentRequirement[],
): Array<{ sectionName: string; items: SortItem[] }> {
  const groups = new Map<string, SortItem[]>();

  const ordered = [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  ordered.forEach((section) => groups.set(section.title, []));

  const push = (name: string, item: SortItem, order: number) => {
    if (!groups.has(name)) groups.set(name, []);
    (groups.get(name) as Array<SortItem & { _o: number }>).push(
      Object.assign(item, { _o: order }),
    );
  };

  documentRequirements.forEach((requirement) =>
    push(
      requirement.sectionName || UNGROUPED,
      {
        kind: 'requirement',
        id: requirement.id,
        label: requirement.description,
        requirement,
      },
      requirement.displayOrder ?? 0,
    ),
  );

  fields.forEach((field) =>
    push(
      fieldSectionTitle(field, sections),
      { kind: 'field', id: field.id, label: field.label, field },
      field.displayOrder ?? Number.POSITIVE_INFINITY,
    ),
  );

  return [...groups.entries()]
    .filter(([, items]) => items.length > 0)
    .map(([sectionName, items]) => ({
      sectionName,
      items: (items as Array<SortItem & { _o: number }>)
        .slice()
        .sort((a, b) => a._o - b._o),
    }));
}

/**
 * SortingTab — one place to decide the order documents and form fields render in.
 *
 * Both kinds share a single displayOrder sequence per section, which is what lets a form
 * field sit between two documents. Dragging is constrained to within a section; section
 * membership is still set on the item itself (requirement sectionName, field sectionId).
 *
 * Nothing is persisted here — the parent's Spara writes the form config and flushes
 * requirement order through the bulk endpoint, so one save covers both kinds.
 */
const SortingTab: React.FC<SortingTabProps> = ({
  fields,
  sections,
  documentRequirements,
  onChange,
  disabled = false,
}) => {
  const groups = buildGroups(fields, sections, documentRequirements);

  /** Write a group's new item order back as a 0..n-1 displayOrder on each entity. */
  const applyItemOrder = (sectionName: string, items: SortItem[]) => {
    const orderById = new Map(items.map((item, index) => [item.id, index]));

    onChange({
      sections,
      documentRequirements: documentRequirements.map((requirement) =>
        orderById.has(requirement.id)
          ? { ...requirement, displayOrder: orderById.get(requirement.id)! }
          : requirement,
      ),
      fields: fields.map((field) =>
        orderById.has(field.id) && fieldSectionTitle(field, sections) === sectionName
          ? { ...field, displayOrder: orderById.get(field.id)! }
          : field,
      ),
    });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    if (result.type === 'SECTION') {
      const reordered = groups.map((g) => g.sectionName);
      const [moved] = reordered.splice(result.source.index, 1);
      if (!moved) return;
      reordered.splice(result.destination.index, 0, moved);

      const position = new Map(reordered.map((title, index) => [title, index]));
      onChange({
        fields,
        documentRequirements,
        sections: sections.map((section) =>
          position.has(section.title)
            ? { ...section, order: position.get(section.title)! }
            : section,
        ),
      });
      return;
    }

    // Items may only move within their own section.
    if (result.destination.droppableId !== result.source.droppableId) return;

    const sectionName = result.source.droppableId.slice(ITEM_DROPPABLE_PREFIX.length);
    const group = groups.find((g) => g.sectionName === sectionName);
    if (!group) return;

    const items = [...group.items];
    const [moved] = items.splice(result.source.index, 1);
    if (!moved) return;
    items.splice(result.destination.index, 0, moved);

    applyItemOrder(sectionName, items);
  };

  if (groups.length === 0) {
    return (
      <Alert severity="info">
        Lägg till dokumentkrav eller formulärfält först — de visas här när de finns.
      </Alert>
    );
  }

  const unregistered = groups.filter(
    (g) => !sections.some((s) => s.title === g.sectionName),
  );

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 2
        }}>
        Dra för att ändra ordningen. Dokument och formulärfält visas i samma ordning för
        lantbrukaren — dra ett fält mellan två dokument om det hör hemma där.
      </Typography>

      {unregistered.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {unregistered.map((g) => g.sectionName).join(', ')}{' '}
          {unregistered.length === 1 ? 'saknas' : 'saknas'} som sektion och visas sist.
          Lägg till den under Sektioner för att kunna flytta den.
        </Alert>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={SECTION_DROPPABLE} type="SECTION">
          {(sectionsProvided) => (
            <Box ref={sectionsProvided.innerRef} {...sectionsProvided.droppableProps}>
              {groups.map((group, sectionIndex) => (
                <Draggable
                  key={group.sectionName}
                  draggableId={`section-${group.sectionName}`}
                  index={sectionIndex}
                  isDragDisabled={disabled}
                >
                  {(sectionDrag) => (
                    <Paper
                      ref={sectionDrag.innerRef}
                      {...sectionDrag.draggableProps}
                      elevation={0}
                      sx={{
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '12px',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        {...sectionDrag.dragHandleProps}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1.5,
                          bgcolor: 'grey.100',
                          cursor: disabled ? 'default' : 'grab',
                        }}
                      >
                        {!disabled && <DragIcon fontSize="small" color="action" />}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {group.sectionName}
                        </Typography>
                        <Chip
                          label={group.items.length}
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>

                      <Droppable
                        droppableId={`${ITEM_DROPPABLE_PREFIX}${group.sectionName}`}
                        type="ITEM"
                      >
                        {(itemsProvided) => (
                          <Box
                            ref={itemsProvided.innerRef}
                            {...itemsProvided.droppableProps}
                            sx={{ p: 1 }}
                          >
                            {group.items.map((item, itemIndex) => (
                              <Draggable
                                key={item.id}
                                draggableId={item.id}
                                index={itemIndex}
                                isDragDisabled={disabled}
                              >
                                {(itemDrag, snapshot) => (
                                  <Box
                                    ref={itemDrag.innerRef}
                                    {...itemDrag.draggableProps}
                                    {...itemDrag.dragHandleProps}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      px: 1.5,
                                      py: 1,
                                      mb: 0.5,
                                      borderRadius: '8px',
                                      bgcolor: snapshot.isDragging
                                        ? 'primary.hint'
                                        : 'background.paper',
                                      border: '1px solid',
                                      borderColor: 'grey.200',
                                      cursor: disabled ? 'default' : 'grab',
                                      ...itemDrag.draggableProps.style,
                                    }}
                                  >
                                    {!disabled && (
                                      <DragIcon fontSize="small" color="action" />
                                    )}
                                    {item.kind === 'requirement' ? (
                                      <DocumentIcon
                                        fontSize="small"
                                        sx={{ color: 'primary.main' }}
                                      />
                                    ) : (
                                      <FieldIcon
                                        fontSize="small"
                                        sx={{ color: 'secondary.main' }}
                                      />
                                    )}
                                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                                      {item.label}
                                    </Typography>
                                    <Chip
                                      label={item.kind === 'requirement' ? 'Dokument' : 'Fält'}
                                      size="small"
                                      variant="outlined"
                                      sx={{ ml: 'auto', flexShrink: 0 }}
                                    />
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                            {itemsProvided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {sectionsProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default SortingTab;
